import { useEffect, useState, useRef } from "react";
import { Upload, Loader2, Trash2, Plus, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { ImageUploader } from "@/components/admin/ImageUploader";

const CAT_LABELS = {
  "bedroom-set": "Bedroom Sets",
  "dining-room": "Dining Room",
  "living-room": "Living Room",
  "recliners": "Recliners",
  "mattresses": "Mattresses",
};
const CATS = ["bedroom-set", "dining-room", "living-room", "recliners", "mattresses"];

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("bedroom-set");
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newProd, setNewProd] = useState({ category: "bedroom-set", title: "", image_url: "", price_sale: "" });
  const pollRef = useRef(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, []);

  const items = products.filter((p) => p.category === cat);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/import-catalog", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = res.data.job_id;
      toast.info("Catalog is processing… this can take a minute.");
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/admin/import-status/${jobId}`);
          if (s.data.status === "done") {
            clearInterval(pollRef.current);
            setImporting(false);
            toast.success(`Imported ${s.data.imported} products!`);
            load();
          } else if (s.data.status === "error") {
            clearInterval(pollRef.current);
            setImporting(false);
            toast.error("Import failed: " + (s.data.error || "unknown"));
          }
        } catch (_) {}
      }, 3000);
    } catch (err) {
      setImporting(false);
      toast.error("Upload failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    setProducts((prev) => prev.filter((p) => p.id !== id));
    toast.success("Product removed");
  };

  const toggleFeatured = async (p) => {
    const featured = !p.featured;
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured } : x)));
    try {
      await api.put(`/products/${p.id}`, { featured });
      toast.success(featured ? "Pinned as Hot Buy" : "Unpinned");
    } catch {
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !featured } : x)));
      toast.error("Failed to update");
    }
  };

  const addProduct = async () => {
    if (!newProd.image_url) return toast.error("Add an image first");
    try {
      const res = await api.post("/products", newProd);
      setProducts((prev) => [...prev, res.data]);
      setCat(newProd.category);
      setNewProd({ category: newProd.category, title: "", image_url: "", price_sale: "" });
      setAdding(false);
      toast.success("Product added");
    } catch (err) {
      toast.error("Failed to add product");
    }
  };

  const inputCls = "w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-ad-red focus:outline-none transition-colors";

  return (
    <div className="flex flex-col gap-6">
      {/* Import */}
      <div className="bg-ad-surface border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl uppercase tracking-wide">Import Seasonal Catalog (PDF)</h3>
          <p className="text-white/50 text-sm mt-1">Upload a new catalog PDF and we'll auto-create product cards. Replaces the previous catalog.</p>
        </div>
        <label
          data-testid="import-catalog-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-ad-red text-white text-sm font-bold uppercase tracking-wider cursor-pointer hover:bg-ad-redhover transition-colors whitespace-nowrap"
        >
          {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {importing ? "Processing…" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={handleImport} disabled={importing} />
        </label>
      </div>

      {/* Category tabs + add */}
      <div className="flex flex-wrap items-center gap-2">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`admin-prodcat-${c}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              cat === c ? "bg-ad-red text-white" : "border border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {CAT_LABELS[c]} ({products.filter((p) => p.category === c).length})
          </button>
        ))}
        <button
          onClick={() => { setAdding(!adding); setNewProd((n) => ({ ...n, category: cat })); }}
          data-testid="add-product-btn"
          className="ml-auto flex items-center gap-2 px-4 py-2 border border-dashed border-white/25 text-white/70 hover:border-ad-red hover:text-ad-red transition-colors text-xs font-bold uppercase tracking-wider"
        >
          <Plus size={14} /> Add Product
        </button>
      </div>

      {adding && (
        <div className="bg-ad-surface border border-white/10 p-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Category</label>
            <select className={inputCls} value={newProd.category} onChange={(e) => setNewProd({ ...newProd, category: e.target.value })}>
              {CATS.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
            <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2 mt-4">Title / Model (optional)</label>
            <input className={inputCls} value={newProd.title} onChange={(e) => setNewProd({ ...newProd, title: e.target.value })} placeholder="e.g. 1534 Bedroom Set" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Product Image</label>
            <ImageUploader value={newProd.image_url} onChange={(v) => setNewProd({ ...newProd, image_url: v })} label="Product" testid="upload-new-product" />
            <button onClick={addProduct} data-testid="save-new-product" className="mt-4 px-6 py-3 bg-ad-red text-white text-sm font-bold uppercase tracking-wider hover:bg-ad-redhover transition-colors">Save Product</button>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map((p) => (
          <div key={p.id} className="group relative bg-white border border-white/10 overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[4/5] bg-neutral-100">
              <img src={resolveImage(p.image_url)} alt={p.title} className="w-full h-full object-contain" loading="lazy" />
            </div>
            {p.featured && (
              <span className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-ad-red text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">Hot Buy</span>
            )}
            <div className="absolute top-1.5 right-1.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => toggleFeatured(p)}
                data-testid={`feature-product-${p.id}`}
                className={`h-8 w-8 grid place-items-center transition-colors ${p.featured ? "bg-ad-red text-white" : "bg-black/70 text-white hover:bg-ad-red"}`}
                title={p.featured ? "Unpin" : "Pin as Hot Buy"}
              >
                <Star size={15} fill={p.featured ? "currentColor" : "none"} />
              </button>
              <button
                onClick={() => remove(p.id)}
                data-testid={`remove-product-${p.id}`}
                className="h-8 w-8 grid place-items-center bg-black/70 text-white hover:bg-ad-red transition-colors"
                title="Remove"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {!items.length && <p className="text-white/40 text-center py-10">No products in this category yet.</p>}
    </div>
  );
};
